import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { ChildTableColumn } from '../../models/child-table.models';

@Component({
  selector: 'child-table-column-menu',
  templateUrl: './child-table-column-menu.component.html',
  styleUrls: ['./child-table-column-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChildTableColumnMenuComponent {
  @ViewChild('menuEl', { read: ElementRef }) menuEl!: ElementRef<HTMLElement>;

  @Input() columnKey = '';
  @Input() column!: ChildTableColumn<any>;
  @Input() isReadonly = false;
  @Input() uniqueValues: string[] = [];

  @Output() applyFilter = new EventEmitter<{ operator: string; value: string }>();
  @Output() cancel = new EventEmitter<void>();

  operator = '';
  filterValue = '';

  get nativeElement(): HTMLElement | null {
    return this.menuEl?.nativeElement ?? null;
  }

  constructor(private readonly cdr: ChangeDetectorRef) {}

  onApply(): void {
    this.applyFilter.emit({ operator: this.operator, value: this.filterValue });
  }

  onCancel(): void {
    this.operator = '';
    this.filterValue = '';
    this.cancel.emit();
  }

  private static readonly FOCUSABLE = 'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  focusFirstItem(): void {
    const firstItem = this.menuEl?.nativeElement?.querySelector(ChildTableColumnMenuComponent.FOCUSABLE) as HTMLElement;
    firstItem?.focus();
  }

  onMenuKeydown(event: KeyboardEvent): void {
    event.stopPropagation();
    const menu = this.menuEl?.nativeElement;
    if (!menu) return;
    const items = Array.from(menu.querySelectorAll(ChildTableColumnMenuComponent.FOCUSABLE)) as HTMLElement[];
    if (items.length === 0) return;
    const active = document.activeElement as HTMLElement | null;
    const idx = active ? items.indexOf(active) : -1;
    const focusAt = (i: number) => items[Math.max(0, Math.min(items.length - 1, i))]?.focus();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusAt(idx >= 0 ? (idx + 1) % items.length : 0);
        return;
      case 'ArrowUp':
        event.preventDefault();
        focusAt(idx >= 0 ? (idx - 1 + items.length) % items.length : items.length - 1);
        return;
      case 'Home':
        event.preventDefault();
        focusAt(0);
        return;
      case 'End':
        event.preventDefault();
        focusAt(items.length - 1);
        return;
      case 'Escape':
        event.preventDefault();
        this.cancel.emit();
        return;
      case 'Tab': {
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
        return;
      }
    }
  }
}
